import { useState } from 'react'
import { Card } from 'primereact/card'
import { Button } from 'primereact/button'
import { SubscriptionPlanType } from '@/types'
import { SUBSCRIPTION_PLANS } from '@/constants'
import { useSubscriptionInfo } from '@/hooks/useSubscription'
import { useSession } from 'next-auth/react'

// Format price for display
export const formatPrice = (plan: string, interval: 'monthly' | 'yearly') => {
  const planConfig = SUBSCRIPTION_PLANS[plan.toUpperCase()]
  if (!planConfig?.pricing?.[interval]) {
    return { price: 'Free', period: '' }
  }

  const pricing = planConfig.pricing[interval]
  return {
    price: `$${pricing.price}`,
    period: pricing.interval,
  }
}

// Billing interval toggle component
export const BillingToggle = ({
  billingInterval,
  setBillingInterval,
}: {
  billingInterval: 'monthly' | 'yearly'
  setBillingInterval: (interval: 'monthly' | 'yearly') => void
}) => {
  const toggleBillingInterval = () => {
    setBillingInterval(billingInterval === 'monthly' ? 'yearly' : 'monthly')
  }

  return (
    <div className="flex justify-center mb-8">
      <div className="flex items-center bg-gray-100 dark:bg-gray-800 p-3 rounded-lg">
        <span
          className={`mr-2 ${billingInterval === 'monthly' ? 'font-bold' : ''} dark:text-white`}
        >
          Monthly
        </span>
        <div
          className="relative inline-block w-12 h-6 cursor-pointer"
          onClick={toggleBillingInterval}
        >
          <div
            className={`w-12 h-6 rounded-full transition-colors duration-200 ${billingInterval === 'yearly' ? 'bg-green-500' : 'bg-gray-400'}`}
          ></div>
          <div
            className={`absolute top-1 left-1 bg-white w-4 h-4 rounded-full transition-transform duration-200 transform ${billingInterval === 'yearly' ? 'translate-x-6' : ''}`}
          ></div>
        </div>
        <span
          className={`ml-2 ${billingInterval === 'yearly' ? 'font-bold' : ''} dark:text-white`}
        >
          Yearly{' '}
          <span className="text-xs text-green-600 dark:text-green-400 font-bold">
            Save ~40%
          </span>
        </span>
      </div>
    </div>
  )
}

interface PricingPlansProps {
  currentPlan?: SubscriptionPlanType
  billingInterval?: 'monthly' | 'yearly'
  onUpgradeClick?: (plan: SubscriptionPlanType) => void
}

export const PricingPlans = ({
  currentPlan,
  billingInterval: propBillingInterval,
  onUpgradeClick,
}: PricingPlansProps) => {
  const { data: session } = useSession()
  const { plan, isLoading } = useSubscriptionInfo()
  const [billingInterval, setBillingInterval] = useState<'monthly' | 'yearly'>(propBillingInterval || 'yearly')
  
  // Use the currentPlan prop if provided, otherwise use the subscription info
  const activePlan = currentPlan || (isLoading ? SubscriptionPlanType.Free : plan)
  
  const handleUpgradeClick = (selectedPlan: SubscriptionPlanType) => {
    if (onUpgradeClick) {
      onUpgradeClick(selectedPlan)
    } else {
      // Default implementation
      handlePurchase(selectedPlan)
    }
  }
  
  const handlePurchase = async (selectedPlan: SubscriptionPlanType) => {
    try {
      let planId: string
      
      // Map the subscription plan type to the marketplace plan ID
      switch (selectedPlan) {
        case SubscriptionPlanType.Pro:
          planId = billingInterval === 'yearly' ? 'pro-yearly' : 'pro-monthly'
          break
        case SubscriptionPlanType.Team:
          planId = billingInterval === 'yearly' ? 'team-yearly' : 'team-monthly'
          break
        default:
          return // No purchase flow for free plan
      }
      
      // Redirect to Microsoft Marketplace to initiate subscription
      window.location.href = `/api/v1/subscriptions/get-offer-url?planId=${planId}`
    } catch (error) {
      console.error('Error initiating purchase:', error)
    }
  }
  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
      {/* Free Plan */}
      <Card
        title="Free"
        subTitle="$0"
        className="h-full dark:bg-gray-800 dark:border-gray-700"
      >
        <div className="h-64 flex flex-col justify-between">
          <div>
            <h3 className="font-medium mb-2">Features:</h3>
            <ul className="list-disc pl-5 mb-4">
              <li>2 simultaneously scheduled messages</li>
              <li>7-day message history</li>
              <li className="text-gray-400 dark:text-gray-500 line-through">
                No attachments
              </li>
              <li className="text-gray-400 dark:text-gray-500 line-through">
                No recurring messages
              </li>
            </ul>
          </div>

          <Button
            label={
              activePlan === SubscriptionPlanType.Free
                ? 'Current Plan'
                : 'Get Started Free'
            }
            className="p-button-outlined"
            disabled={activePlan === SubscriptionPlanType.Free || isLoading}
            onClick={() => handleUpgradeClick(SubscriptionPlanType.Free)}
            style={{ 
              display: activePlan !== SubscriptionPlanType.Free && 
                      (activePlan === SubscriptionPlanType.Pro || 
                       activePlan === SubscriptionPlanType.Team || 
                       activePlan === SubscriptionPlanType.Gift) 
                      ? 'none' : 'inline-flex' 
            }}
          />
        </div>
      </Card>

      {/* Pro Plan */}
      <Card
        title="Pro"
        subTitle={`${formatPrice('Pro', billingInterval).price}/${formatPrice('Pro', billingInterval).period}`}
        className={`h-full relative dark:bg-gray-800 dark:border-gray-700 ${activePlan === SubscriptionPlanType.Pro ? 'border-2 border-green-500 dark:border-green-400' : ''}`}
      >
        <div className="absolute -top-3 right-4 bg-green-500 text-white px-3 py-1 rounded-full text-xs font-bold">
          MOST POPULAR
        </div>
        <div className="h-64 flex flex-col justify-between">
          <div>
            <h3 className="font-medium mb-2">Features:</h3>
            <ul className="list-disc pl-5 mb-4">
              <li>Unlimited scheduled messages</li>
              <li>File attachments</li>
              <li>90-day message history</li>
              <li>Recurring messages</li>
              <li>Reusable templates</li>
              <li>Remove "Sent with Delayed Send" branding</li>
            </ul>
          </div>

          <Button
            label={
              activePlan === SubscriptionPlanType.Pro
                ? 'Current Plan'
                : 'Upgrade to Pro'
            }
            icon={activePlan !== SubscriptionPlanType.Pro ? 'pi pi-arrow-up' : undefined}
            className={
              activePlan === SubscriptionPlanType.Pro
                ? 'p-button-success'
                : 'p-button-primary p-button-raised'
            }
            disabled={activePlan === SubscriptionPlanType.Pro || isLoading}
            loading={isLoading}
            onClick={() => handleUpgradeClick(SubscriptionPlanType.Pro)}
          />
        </div>
      </Card>

      {/* Team Plan */}
      <Card
        title="Team"
        subTitle={`${formatPrice('Team', billingInterval).price}/user/${formatPrice('Team', billingInterval).period}`}
        className={`h-full dark:bg-gray-800 dark:border-gray-700 ${activePlan === SubscriptionPlanType.Team ? 'border-2 border-green-500 dark:border-green-400' : ''}`}
      >
        <div className="h-64 flex flex-col justify-between">
          <div>
            <h3 className="font-medium mb-2">Features:</h3>
            <ul className="list-disc pl-5 mb-4">
              <li>Everything in Pro plan</li>
              <li>Minimum 2 users</li>
              <li>Volume discount for larger teams</li>
              <li>Central billing & management</li>
              <li>Team administration panel</li>
              <li>Priority support</li>
            </ul>
          </div>

          <Button
            label={
              activePlan === SubscriptionPlanType.Team
                ? 'Current Plan'
                : 'Upgrade to Team Plan'
            }
            icon={activePlan !== SubscriptionPlanType.Team ? 'pi pi-users' : undefined}
            className={
              activePlan === SubscriptionPlanType.Team
                ? 'p-button-success'
                : 'p-button-primary p-button-raised'
            }
            disabled={activePlan === SubscriptionPlanType.Team || isLoading}
            loading={isLoading}
            onClick={() => handleUpgradeClick(SubscriptionPlanType.Team)}
          />
        </div>
      </Card>
    </div>
  )
}